import { LowerCasePipe } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild, effect } from '@angular/core';
import { Router } from '@angular/router';
import { GlobalStore } from '@maximizer/core/shared/data-access';
import { UserRights } from '@maximizer/core/shared/domain';
import { UI_LOADER } from '@maximizer/core/shared/ui/components/loader';
import { UI_KENDO } from '@maximizer/core/shared/ui/kendo';
import { CaseService } from '@maximizer/outlook/case/data-access';
import { CaseCardComponent } from '@maximizer/outlook/case/ui';
import { EmailDetailComponent } from '@maximizer/outlook/interaction-log/ui';
import {
  RelatedService,
  haveSameEntryKeys,
} from '@maximizer/outlook/main/data-access';
import { OpportunityService } from '@maximizer/outlook/opportunity/data-access';
import { OpportunityCardComponent } from '@maximizer/outlook/opportunity/ui';
import {
  OutlookService,
  OutlookStore,
  TrackerService,
  VerticalService,
} from '@maximizer/outlook/shared/data-access';
import {
  CaseCard,
  EntryType,
  EventAction,
  EventJourney,
  EventModule,
  MIN_SEARCH_LENGTH,
  OpportunityCard,
  OutlookEmailDetail,
} from '@maximizer/outlook/shared/domain';
import {
  OUTLOOK_SHARED_UI,
  OutlookNotificationComponent,
} from '@maximizer/outlook/shared/ui';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { KENDO_BUTTON } from '@progress/kendo-angular-buttons';
import { KENDO_TEXTBOX } from '@progress/kendo-angular-inputs';
import { catchError, finalize, forkJoin, Observable, of, Subject } from 'rxjs';

@Component({
  selector: 'maximizer-outlook-related',
  templateUrl: './related.component.html',
  styleUrls: ['./related.component.scss'],
  imports: [
    CaseCardComponent,
    EmailDetailComponent,
    KENDO_BUTTON,
    KENDO_TEXTBOX,
    LowerCasePipe,
    OpportunityCardComponent,
    OUTLOOK_SHARED_UI,
    TranslatePipe,
    UI_KENDO,
    UI_LOADER,
  ],
})
export class OutlookRelatedComponent implements OnInit, OnDestroy {
  @ViewChild('notification')
  notification!: OutlookNotificationComponent;

  private readonly searchSubject = new Subject<string>();
  private readonly destroy$ = new Subject<void>();
  readonly MIN_SEARCH_LENGTH = MIN_SEARCH_LENGTH;

  EntryType = EntryType;
  currentAbEntryIds: string[] = [];
  opportunities: OpportunityCard[] = [];
  cases: CaseCard[] = [];
  userRights?: UserRights;
  saveEmailDetail = false;
  saveEmailDetailProperties?: OutlookEmailDetail;
  searchPlaceholder = '';
  searchInput = '';
  searchPhrase?: string;
  trackingEntryType: EntryType = EntryType.Opportunity;

  isLoading = {
    spinner: false,
    skeleton: false,
  };

  constructor(
    private readonly translate: TranslateService,
    private readonly opportunityService: OpportunityService,
    private readonly caseService: CaseService,
    private readonly outlookService: OutlookService,
    private readonly router: Router,
    private readonly tracker: TrackerService,
    private readonly relatedService: RelatedService,
    private readonly verticalService: VerticalService,
    public globalStore: GlobalStore,
    public outlookStore: OutlookStore,
  ) {
    effect(() =>
      this.reloadOnEntryKeysChange(this.relatedService.relatedEntryKeys()),
    );
  }

  private reloadOnEntryKeysChange(entryKeys: string[]): void {
    const isSearching = !!this.searchPhrase;
    if (isSearching || haveSameEntryKeys(entryKeys, this.currentAbEntryIds)) {
      return;
    }

    this.isLoading.skeleton = true;
    this.loadEntryKeys(entryKeys);
  }

  ngOnInit(): void {
    this.isLoading.skeleton = true;
    this.setUserRights();
    this.setupSearchSubject();

    const existingSearchContext = this.outlookStore
      .relatedSearchPhrase()
      ?.trim();
    if (
      existingSearchContext &&
      existingSearchContext.length >= MIN_SEARCH_LENGTH
    ) {
      this.searchInput = existingSearchContext;
      this.performSearch(existingSearchContext);
    } else {
      if (existingSearchContext) {
        this.outlookStore.clearRelatedSearchContext();
      }
      this.loadData();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setupSearchSubject(): void {
    this.relatedService.setupSearchSubject(
      this.searchSubject,
      this.destroy$,
      (searchTerm) => this.performSearch(searchTerm),
      MIN_SEARCH_LENGTH,
    );
  }

  get cardMode(): 'read' | 'compose' {
    return this.relatedService.isComposeMode ? 'compose' : 'read';
  }

  async getSavedEmails(): Promise<void> {
    this.isLoading.skeleton = true;
    try {
      const allEmails = await this.relatedService.getSavedEmails();

      this.relatedService.markSavedOpportunities(
        this.opportunities,
        allEmails.opportunities,
      );
      this.relatedService.markSavedCases(this.cases, allEmails.cases);
    } catch (error) {
      console.error('Error retrieving saved emails:', error);
    } finally {
      this.isLoading.skeleton = false;
    }
  }

  setUserRights(): void {
    if (!this.globalStore.session()) {
      return;
    }
    this.userRights = this.globalStore.session()?.user.rights;

    this.updateSearchPlaceholder();
  }

  private updateSearchPlaceholder(): void {
    if (!this.userRights?.opportunities?.read) {
      this.searchPlaceholder = this.translate.instant(
        'outlook.main.related.searchPlaceholderSingle',
        {
          label: this.verticalService.translateVertical('cases'),
        },
      );
    } else if (this.userRights?.customerService?.read) {
      this.searchPlaceholder = this.translate.instant(
        'outlook.main.related.searchPlaceholder',
        {
          oppLabel: this.translate.instant('outlook.entries.opportunities'),
          caseLabel: this.verticalService.translateVertical('cases'),
        },
      );
    } else {
      this.searchPlaceholder = this.translate.instant(
        'outlook.main.related.searchPlaceholderSingle',
        {
          label: this.translate.instant('outlook.entries.opportunities'),
        },
      );
    }
  }

  loadData(): void {
    this.loadEntryKeys(this.relatedService.relatedEntryKeys());
  }

  private loadEntryKeys(entryKeys: string[]): void {
    this.currentAbEntryIds = entryKeys;

    if (entryKeys.length === 0) {
      this.opportunities = [];
      this.cases = [];
      this.isLoading.skeleton = false;
      return;
    }

    const requests: {
      opportunities?: Observable<OpportunityCard[]>;
      cases?: Observable<CaseCard[]>;
    } = {};

    if (this.userRights?.opportunities.read) {
      requests.opportunities = this.opportunityService
        .getOpportunityByContactId(this.currentAbEntryIds)
        .pipe(
          catchError((error) => {
            console.error('Error fetching opportunities:', error);
            return of([]);
          }),
        );
    }

    if (this.userRights?.customerService.read) {
      requests.cases = this.caseService
        .getCaseByContactId(this.currentAbEntryIds)
        .pipe(
          catchError((error) => {
            console.error('Error fetching cases:', error);
            return of([]);
          }),
        );
    }

    forkJoin(requests)
      .pipe(
        finalize(() => {
          if (entryKeys === this.currentAbEntryIds) {
            this.isLoading.skeleton = false;
          }
        }),
      )
      .subscribe((result) => {
        // A newer load replaced currentAbEntryIds; this response is stale.
        if (entryKeys !== this.currentAbEntryIds) return;

        if (result.opportunities) {
          this.opportunities = result.opportunities;
        }

        if (result.cases) {
          this.cases = result.cases;
        }

        if (this.cases.length > 0 || this.opportunities.length > 0) {
          this.getSavedEmails();
        }
      });
  }

  private performSearch(searchTerm: string): void {
    this.searchPhrase = searchTerm;
    this.isLoading.skeleton = true;

    const requests: {
      opportunities?: Observable<OpportunityCard[]>;
      cases?: Observable<CaseCard[]>;
    } = {};

    if (this.userRights?.opportunities?.read) {
      requests.opportunities =
        this.relatedService.searchOpportunities(searchTerm);
    }

    if (this.userRights?.customerService?.read) {
      requests.cases = this.relatedService.searchCases(searchTerm);
    }

    forkJoin(requests)
      .pipe(
        finalize(() => {
          this.isLoading.skeleton = false;
          if (this.cases.length > 0 || this.opportunities.length > 0) {
            this.getSavedEmails();
          }
        }),
      )
      .subscribe((result) => {
        if (result.opportunities) {
          this.opportunities = result.opportunities;
        }

        if (result.cases) {
          this.cases = result.cases;
        }
      });
  }

  private clearSearch(): void {
    this.isLoading.skeleton = true;
    this.opportunities = [];
    this.cases = [];
    this.searchPhrase = undefined;
    this.outlookStore.clearRelatedSearchContext();
  }

  onSearchInput(value: string): void {
    this.searchInput = value;
    if (value.length === 0) {
      // Push the empty value so debounceTime supersedes any pending term.
      this.searchSubject.next(value);
      if (this.searchPhrase) {
        this.clearSearch();
        this.loadData();
      }
      this.outlookStore.clearRelatedSearchContext();
    } else {
      this.searchSubject.next(value);

      const trimmedValue = value.trim();
      if (trimmedValue.length > 0) {
        this.outlookStore.setRelatedSearchContext(trimmedValue);
      } else {
        this.outlookStore.clearRelatedSearchContext();
      }
    }
  }

  async saveEmail(key: string, type: EntryType): Promise<void> {
    this.trackingEntryType = type;

    if (this.outlookStore.preferences().saveEmailDetail) {
      await this.openSaveEmailDetails(key, type);
    } else {
      await this.performSaveEmail(key, type);
    }
  }

  async openSaveEmailDetails(key: string, type: EntryType): Promise<void> {
    // Properties must be resolved before the dialog renders: EmailDetailComponent
    // reads its emailProperties input once when its field options arrive.
    this.saveEmailDetailProperties = {
      subject: await this.outlookService.getSubject(),
      type,
      key,
    };
    this.saveEmailDetail = true;
  }

  private async performSaveEmail(key: string, type: EntryType) {
    this.isLoading.spinner = true;

    try {
      const result = await this.relatedService.performSaveEmail(key, type);

      if (!result.success) {
        this.notification.showMessage(
          'error',
          result.error ?? 'outlook.emailNotification.failEntry',
          true,
        );
        return;
      }

      this.showNotification(type);
      this.markEntrySaved(key, type);
    } catch (error) {
      console.error('Error during saveEmail:', error);
      this.notification.showMessage(
        'error',
        'outlook.emailNotification.failEntry',
        true,
      );
    } finally {
      this.isLoading.spinner = false;
    }
  }

  async onSaveEmailDetail(key: string, type: EntryType): Promise<void> {
    this.saveEmailDetail = false;
    this.saveEmailDetailProperties = undefined;

    this.showNotification(type);
    this.markEntrySaved(key, type);

    await this.outlookService.saveOpportunityOrCaseInOutlook(key, type);
  }

  private markEntrySaved(key: string, type: EntryType): void {
    const entries =
      type === EntryType.Opportunity ? this.opportunities : this.cases;
    this.relatedService.updateSavedEmailStatus(entries, key);
  }

  showAll(type: EntryType): void {
    this.tracker.trackEvent({
      module: EventModule.RelatedRecords,
      journey: EventJourney.ShowMore,
      action: EventAction.Clicked,
      metadata: { entity: type },
    });

    const trimmedSearchInput = this.searchInput.trim();
    if (trimmedSearchInput.length >= MIN_SEARCH_LENGTH) {
      this.outlookStore.setRelatedSearchContext(trimmedSearchInput);
    } else {
      this.outlookStore.clearRelatedSearchContext();
    }
    this.router.navigate([`related/${type}/detail`]);
  }

  private showNotification(type: EntryType) {
    if (type === EntryType.Opportunity) {
      const message = this.translate.instant(
        'outlook.emailNotification.successOpportunity',
        {
          oppLabel: this.translate.instant('outlook.entries.opportunity'),
        },
      );

      this.notification.showMessage('success', message);
    }

    if (type === EntryType.Case) {
      const message = this.translate.instant(
        'outlook.emailNotification.successCase',
        {
          caseLabel: this.verticalService.translateVertical('case'),
        },
      );

      this.notification.showMessage('success', message);
    }
  }

  getResultsCountString(): string {
    if (!this.userRights?.opportunities?.read) {
      return this.translate.instant('outlook.main.related.result', {
        count: this.cases.length,
        label: this.verticalService.translateVertical('cases'),
      });
    } else if (this.userRights?.customerService?.read) {
      return this.translate.instant('outlook.main.related.results', {
        oppCount: this.opportunities.length,
        caseCount: this.cases.length,
        oppLabel: this.translate.instant('outlook.entries.opportunities'),
        caseLabel: this.verticalService.translateVertical('cases'),
      });
    } else {
      return this.translate.instant('outlook.main.related.result', {
        count: this.opportunities.length,
        label: this.translate.instant('outlook.entries.opportunities'),
      });
    }
  }
}
